import { useState } from "react";
import { Routes, Route, Link, useLocation } from "react-router-dom";
import { 
  Calendar, 
  CalendarDays, 
  Mail, 
  LogOut, 
  Menu, 
  X,
  Clock,
  Users,
  LayoutDashboard,
  Images,
  Ticket,
  Building,
  Settings
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import axios from "axios";

import { AdminDashboard } from "./admin/AdminDashboard";
import { AdminBookings } from "./admin/AdminBookings";
import { AdminEvents } from "./admin/AdminEvents";
import { AdminMessages } from "./admin/AdminMessages";
import { AdminGallery } from "./admin/AdminGallery";
import { AdminTicketBookings } from "./admin/AdminTicketBookings";
import { AdminInternalBooking } from "./admin/AdminInternalBooking";
import { AdminSettings } from "./admin/AdminSettings";
import { AdminStaffRota } from "./admin/AdminStaffRota";
import { StaffMyShifts } from "./admin/StaffMyShifts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [userRole, setUserRole] = useState(null);
  const [authError, setAuthError] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);

  const [staffInfo, setStaffInfo] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!credentials.username || !credentials.password) {
      setAuthError("Please enter username and password");
      return;
    }
    
    setLoginLoading(true);
    setAuthError("");
    
    try {
      const auth = btoa(`${credentials.username}:${credentials.password}`);
      
      // First try admin/internal/viewer login
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Basic ${auth}` }
        });
        setUserRole(response.data.role);
        setIsAuthenticated(true);
        return;
      } catch (adminError) {
        // If admin login fails, try staff login
        if (adminError.response?.status === 401) {
          try {
            const staffResponse = await axios.post(`${API}/staff/login`, {}, {
              headers: { Authorization: `Basic ${auth}` }
            });
            setUserRole("staff");
            setStaffInfo(staffResponse.data);
            setIsAuthenticated(true);
            return;
          } catch (staffError) {
            throw new Error("Invalid credentials");
          }
        }
        throw adminError;
      }
    } catch (error) {
      console.error("Login error:", error);
      setAuthError("Invalid username or password");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setCredentials({ username: "", password: "" });
    setUserRole(null);
    setStaffInfo(null);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center py-12 px-4" data-testid="admin-login">
        <Card className="card-heritage w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-[#14532D] rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-2xl font-serif">E</span>
            </div>
            <CardTitle className="font-serif text-2xl">Admin Login</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="username" className="label-heritage">Username</Label>
                <Input
                  id="username"
                  type="text"
                  value={credentials.username}
                  onChange={(e) => setCredentials(prev => ({ ...prev, username: e.target.value }))}
                  className="input-heritage"
                  placeholder="Enter username"
                  data-testid="admin-username"
                />
              </div>
              <div>
                <Label htmlFor="password" className="label-heritage">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials(prev => ({ ...prev, password: e.target.value }))}
                  className="input-heritage"
                  placeholder="Enter password"
                  data-testid="admin-password"
                />
              </div>
              {authError && (
                <p className="text-sm text-[#B91C1C]">{authError}</p>
              )}
              <Button type="submit" className="btn-primary w-full" disabled={loginLoading} data-testid="admin-login-btn">
                {loginLoading ? "Logging in..." : "Login"}
              </Button>
              <div className="text-xs text-[#78716C] text-center mt-4">
                <p><strong>admin</strong> - Full access</p>
                <p><strong>internal</strong> - Internal bookings only</p>
                <p><strong>viewer</strong> - View only access</p>
                <p><strong>staff</strong> - View your shifts</p>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F0] flex" data-testid="admin-dashboard">
      {/* Mobile sidebar toggle */}
      <button
        className="lg:hidden fixed top-20 left-4 z-50 p-2 bg-[#1C1917] text-white rounded-lg"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        data-testid="admin-sidebar-toggle"
      >
        {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Sidebar */}
      <aside 
        className={`admin-sidebar fixed lg:relative inset-y-0 left-0 z-40 transform ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0 transition-transform flex-shrink-0`}
        data-testid="admin-sidebar"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-[#14532D] rounded-full flex items-center justify-center">
            <span className="text-white font-bold font-serif">E</span>
          </div>
          <div>
            <h2 className="font-bold font-serif">Admin Panel</h2>
            <p className="text-xs text-[#78716C]">Elizabethan Centre</p>
          </div>
        </div>
        
        {/* Role badge */}
        <div className="mb-6 px-3 py-2 bg-[#F5F5F0] rounded-lg">
          <p className="text-xs text-[#78716C]">Logged in as</p>
          <p className="font-semibold text-[#1C1917]">{userRole === 'staff' && staffInfo ? staffInfo.name : credentials.username}</p>
          <span className={`text-xs px-2 py-0.5 rounded ${
            userRole === 'admin' ? 'bg-[#14532D] text-white' :
            userRole === 'internal' ? 'bg-[#D97706] text-white' :
            userRole === 'staff' ? 'bg-[#3B82F6] text-white' :
            'bg-[#6B7280] text-white'
          }`}>
            {userRole === 'admin' ? 'Full Access' : 
             userRole === 'internal' ? 'Internal Only' : 
             userRole === 'staff' ? 'Staff' :
             'View Only'}
          </span>
        </div>

        <nav className="space-y-2">
          {userRole === 'staff' && (
            <AdminNavLink to="/admin/my-shifts" icon={<Clock size={20} />} label="My Shifts" />
          )}
          {userRole !== 'staff' && (
            <AdminNavLink to="/admin" icon={<LayoutDashboard size={20} />} label="Dashboard" exact />
          )}
          {(userRole === 'admin' || userRole === 'viewer') && (
            <AdminNavLink to="/admin/bookings" icon={<Calendar size={20} />} label="Bookings" />
          )}
          {(userRole === 'admin' || userRole === 'internal') && (
            <AdminNavLink to="/admin/internal-booking" icon={<Building size={20} />} label="Internal Booking" />
          )}
          {(userRole === 'admin' || userRole === 'viewer') && (
            <AdminNavLink to="/admin/events" icon={<CalendarDays size={20} />} label="Events" />
          )}
          {(userRole === 'admin' || userRole === 'viewer') && (
            <AdminNavLink to="/admin/tickets" icon={<Ticket size={20} />} label="Ticket Bookings" />
          )}
          {(userRole === 'admin' || userRole === 'viewer') && (
            <AdminNavLink to="/admin/gallery" icon={<Images size={20} />} label="Gallery" />
          )}
          {(userRole === 'admin' || userRole === 'viewer') && (
            <AdminNavLink to="/admin/messages" icon={<Mail size={20} />} label="Messages" />
          )}
          {userRole === 'admin' && (
            <AdminNavLink to="/admin/staff-rota" icon={<Users size={20} />} label="Staff Rota" />
          )}
          {userRole === 'admin' && (
            <AdminNavLink to="/admin/settings" icon={<Settings size={20} />} label="Settings" />
          )}
        </nav>

        <div className="mt-auto pt-8 border-t border-[#292524] mt-8">
          <button
            onClick={handleLogout}
            className="admin-nav-item w-full text-[#B91C1C]"
            data-testid="admin-logout-btn"
          >
            <LogOut size={20} />
            Logout
          </button>
        </div>
      </aside>

      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <main className="flex-1 p-6 lg:p-8 pt-20 lg:pt-8 min-h-screen">
        <Routes>
          <Route index element={userRole === 'staff' ? <StaffMyShifts credentials={credentials} staffInfo={staffInfo} /> : <AdminDashboard credentials={credentials} userRole={userRole} />} />
          <Route path="my-shifts" element={<StaffMyShifts credentials={credentials} staffInfo={staffInfo} />} />
          <Route path="bookings" element={<AdminBookings credentials={credentials} userRole={userRole} />} />
          <Route path="internal-booking" element={<AdminInternalBooking credentials={credentials} userRole={userRole} />} />
          <Route path="events" element={<AdminEvents credentials={credentials} userRole={userRole} />} />
          <Route path="tickets" element={<AdminTicketBookings credentials={credentials} userRole={userRole} />} />
          <Route path="gallery" element={<AdminGallery credentials={credentials} userRole={userRole} />} />
          <Route path="messages" element={<AdminMessages credentials={credentials} userRole={userRole} />} />
          <Route path="staff-rota" element={<AdminStaffRota credentials={credentials} userRole={userRole} />} />
          <Route path="settings" element={<AdminSettings credentials={credentials} userRole={userRole} />} />
        </Routes>
      </main>
    </div>
  );
}

function AdminNavLink({ to, icon, label, exact = false }) {
  const location = useLocation();
  const isActive = exact 
    ? location.pathname === to 
    : location.pathname.startsWith(to) && (to !== "/admin" || location.pathname === "/admin");

  return (
    <Link
      to={to}
      className={`admin-nav-item ${isActive ? "active" : ""}`}
      data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {icon}
      {label}
    </Link>
  );
}
